import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import EntitySchemaImplementingSSISelectionWithRestrictions from 'bulk-import/panes/EntitySchemaImplementingSSISelectionWithRestrictions';
import RegistryCollectionSelection from 'bulk-import/panes/RegistryCollectionSelection';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {
  OPTIONS,
  RegistryImportSettingsProps,
  SelectedSchemaIds,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportOption from 'bulk-import/SpreadsheetBulkImportOption';
import {BulkImportOptions, SelectedDestination} from 'bulk-import/SpreadsheetBulkImportStore';
import {isSequenceSchema} from 'bulk-import/SpreadsheetBulkImportUtil';
import {RegistryLabelingStrategy, Registry} from 'constants/RegistryConstants';
import LabelingStrategyInput from 'registry/labeling-strategies/LabelingStrategyInput';
import ProjectFolderField from 'registry/ProjectFolderField';
import RegistrySelector from 'registry/RegistrySelector';
import {
  TRIAL_SAMPLE_SCHEMA_INTERFACE_ID,
  TRIAL_SUBJECT_SCHEMA_INTERFACE_ID,
} from 'registry/trial-interfaces/TrialInterfaceConstants';
import ContainerSchemaSelector from 'samples/ContainerSchemaSelector';
import {ContainerSchema} from 'samples/storables/StorableSchemaConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import Modal from 'ui-library/modals/Modal';

export const DATA_TEST_KEYS = {
  TRIAL_SAMPLE_ENTITY_SCHEMA_SELECTION: 'trialSampleEntitySchemaSelection',
  TRIAL_SAMPLE_LABELING_STRATEGY_INPUT: 'trialSampleLabelingStrategyInput',
  TRIAL_SUBJECT_ENTITY_SCHEMA_SELECTION: 'trialSubjectEntitySchemaSelection',
  TRIAL_SUBJECT_LABELING_STRATEGY_INPUT: 'trialSubjectLabelingStrategyInput',
};

import ModalButtonKey from '../../test/lib/cypress/shared/ModalButtonKey';

export interface ChooseImportTrialSamplesModalPaneProps extends RegistryImportSettingsProps {
  modalProps: {
    title: string;
    onRequestClose: () => void;
  };
  options: BulkImportOptions;
  selectedDestination: SelectedDestination | null;
  selectedSchemaIds: SelectedSchemaIds;
}

export default function ChooseImportTrialSamplesModalPane(
  props: ChooseImportTrialSamplesModalPaneProps
): JSX.Element {
  const handleRegistryChange = (e: {target: {value: Registry | null}}): void => {
    const registry = e.target.value;
    SpreadsheetBulkImportActions.setImportSetting({registryImportSettings: {registry}});
  };
  const handleDestinationFolderChange = (e: {target: {value: string}}): void => {
    // `selectedDestination` is used for selecting either a folder OR registry in other flows. We re-use
    // this setting, but ignore `selectedDestination.registryId`, since the selected registry is stored
    // in `registryImportSettings.registry`.
    SpreadsheetBulkImportActions.setImportSetting({
      selectedDestination: {folderId: e.target.value, registryId: null},
    });
  };

  const handleTrialSampleLabelingStrategyChange = (labelingStrategy: RegistryLabelingStrategy): void => {
    SpreadsheetBulkImportActions.setImportSetting({registryImportSettings: {labelingStrategy}});
  };

  const handleTrialSubjectLabelingStrategyChange = (
    trialSubjectLabelingStrategy: RegistryLabelingStrategy
  ): void => {
    SpreadsheetBulkImportActions.setImportSetting({registryImportSettings: {trialSubjectLabelingStrategy}});
  };

  const handleContainerSchemaChange = (selectedContainerSchema: ContainerSchema): void => {
    SpreadsheetBulkImportActions.setImportSetting({
      selectedContainerSchemaId: selectedContainerSchema.id,
    });
  };

  const isAtLeastOneCollectionSelected = (): boolean => props.inOrgCollection || props.inUserCollection;

  const folderId = props.selectedDestination ? props.selectedDestination.folderId : null;

  // Roughly in the order that they appear in the modal
  const allRequiredPropsSet =
    props.registry &&
    isAtLeastOneCollectionSelected() &&
    props.selectedSchemaIds.containerSchemaId &&
    props.selectedSchemaIds.tagSchemaId && // Trial sample schema
    props.labelingStrategy && // Trial sample labeling strategy
    props.selectedSchemaIds.trialSubjectSchemaId &&
    props.trialSubjectLabelingStrategy &&
    folderId != null;

  return (
    <Modal
      enableRedesign={false}
      buttons={[
        {
          key: 'next-from-choose-spreadsheet-btn',
          text: 'Next',
          style: ButtonStyle.SECONDARY,
          onClick: SpreadsheetBulkImportActions.goToNextPane,
          disabled: !allRequiredPropsSet,
          dataTestAttrs: {
            component: 'UploadSpreadsheetModal',
            element: 'footerButton',
            key: ModalButtonKey.NEXT,
          },
        },
      ]}
      {...props.modalProps}
    >
      <Row className={css(styles.gap)}>
        <Col xs={12}>
          <FieldLabel label="Registry" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <RegistrySelector
            onChange={handleRegistryChange}
            registryId={props.registry ? props.registry.id : null}
          />
        </Col>
      </Row>
      <RegistryCollectionSelection
        className={css(styles.gap)}
        registry={props.registry}
        inOrgCollection={props.inOrgCollection}
        inUserCollection={props.inUserCollection}
      />
      <Row className={css(styles.gap)}>
        <Col xs={12}>
          <FieldLabel label="Container schema" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <ContainerSchemaSelector
            onChange={handleContainerSchemaChange}
            registryId={props.registry?.id}
            selectedContainerSchemaId={props.selectedSchemaIds.containerSchemaId}
            shouldShowNoSchemaOption={false}
            shouldDefaultIfSingleItem={true}
          />
        </Col>
      </Row>
      {/* Start trial sample schema selection */}
      <div className={classNames(css(styles.schemaAndLabelingStrategyContainer), css(styles.gap))}>
        <Row className={props.selectedSchemaIds.tagSchemaId ?? css(styles.lastChildMarginBottom)}>
          <Col xs={12}>
            <EntitySchemaImplementingSSISelectionWithRestrictions
              label="Trial sample schema"
              registry={props.registry}
              onRequestClose={props.modalProps.onRequestClose}
              selectedSchemaId={props.selectedSchemaIds.tagSchemaId ?? null}
              includedInterfaceIds={[TRIAL_SAMPLE_SCHEMA_INTERFACE_ID]}
              shouldDefaultIfSingleItem={true}
              importSettingsKey="selectedTagSchemaId"
              data-test-key={DATA_TEST_KEYS.TRIAL_SAMPLE_ENTITY_SCHEMA_SELECTION}
            />
          </Col>
        </Row>
        {props.selectedSchemaIds.tagSchemaId && isSequenceSchema(props.selectedSchemaIds.tagSchemaId) && (
          <Row>
            <Col xs={12}>
              <FieldLabel label="DNA Sequence settings" />
              <SpreadsheetBulkImportOption
                helpContent={
                  <div>
                    If this option is selected, imported DNA sequences will be circular. If not, imported DNA
                    sequences will be linear.
                  </div>
                }
                isChecked={props.options[OPTIONS.ARE_SEQUENCES_CIRCULAR]}
                name={OPTIONS.ARE_SEQUENCES_CIRCULAR}
                text="Import circular DNA sequences"
              />
            </Col>
          </Row>
        )}
        {props.selectedSchemaIds.tagSchemaId && (
          <Row>
            <Col xs={12}>
              <LabelingStrategyInput
                labelingStrategy={props.labelingStrategy}
                labelingStrategies={props.labelingStrategyOptions}
                onChange={handleTrialSampleLabelingStrategyChange}
                tagSchemaIds={[props.selectedSchemaIds.tagSchemaId]}
                data-test-key={DATA_TEST_KEYS.TRIAL_SAMPLE_LABELING_STRATEGY_INPUT}
              />
            </Col>
          </Row>
        )}
      </div>
      {/* End trial sample schema selection */}
      {/* Start trial subject schema selection */}
      <div className={classNames(css(styles.schemaAndLabelingStrategyContainer), css(styles.gap))}>
        <Row className={props.selectedSchemaIds.trialSubjectSchemaId ?? css(styles.lastChildMarginBottom)}>
          <Col xs={12}>
            <EntitySchemaImplementingSSISelectionWithRestrictions
              label="Trial subject schema"
              registry={props.registry}
              onRequestClose={props.modalProps.onRequestClose}
              selectedSchemaId={props.selectedSchemaIds.trialSubjectSchemaId ?? null}
              includedInterfaceIds={[TRIAL_SUBJECT_SCHEMA_INTERFACE_ID]}
              shouldDefaultIfSingleItem={true}
              importSettingsKey="selectedTrialSubjectSchemaId"
              data-test-key={DATA_TEST_KEYS.TRIAL_SUBJECT_ENTITY_SCHEMA_SELECTION}
            />
          </Col>
        </Row>
        {props.selectedSchemaIds.trialSubjectSchemaId && (
          <Row>
            <Col xs={12}>
              <LabelingStrategyInput
                labelingStrategy={props.trialSubjectLabelingStrategy}
                labelingStrategies={props.trialSubjectLabelingStrategyOptions!}
                onChange={handleTrialSubjectLabelingStrategyChange}
                tagSchemaIds={[props.selectedSchemaIds.trialSubjectSchemaId]}
                data-test-key={DATA_TEST_KEYS.TRIAL_SUBJECT_LABELING_STRATEGY_INPUT}
              />
            </Col>
          </Row>
        )}
      </div>
      {/* End trial subject schema selection */}
      <Row>
        <Col xs={12}>
          <ProjectFolderField
            folderId={folderId}
            labelStyle={LabelStyle.LABEL}
            onChange={handleDestinationFolderChange}
            isRequired={true}
          />
        </Col>
      </Row>
    </Modal>
  );
}

const styles = StyleSheet.create({
  lastChildMarginBottom: {
    marginBottom: '16px',
  },
  gap: {
    marginBottom: '16px',
  },
  schemaAndLabelingStrategyContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    borderRadius: '5px',
    // No bottom padding due to the margin on the last child provided by LabelingStrategyInput
    padding: '16px 16px 0 16px',
    border: `1px solid ${GRAY_LIGHT}`,
  },
});
